import React, { useEffect, useState, useContext } from "react";
import { useNavigate, useSearchParams, useParams } from "react-router-dom";
import {
  Typography,
  TextField,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Link,
  Button,
} from "@mui/material";
import { Add as AddIcon } from "@mui/icons-material";
import apiClient from "../utils/apiClient";
import AuthContext from "../auth/AuthContext";
import RecipeForm from "../components/RecipeForm";
import { getRecipeUrl } from "../utils/recipeUtils";
import "../style/Crops.css";

const Recipes = () => {
  const { creator } = useParams();
  const [recipes, setRecipes] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [recipeFormOpen, setRecipeFormOpen] = useState(false);
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { user } = useContext(AuthContext);

  const showchildren = searchParams.get("showchildren");
  const tagParam = searchParams.get("tag");

  const fetchRecipes = async () => {
    try {
      let url;
      if (creator) {
        // Fetch recipes by creator
        url = `/recipes/${creator}`;
      } else {
        url = "/recipes";
        const params = new URLSearchParams();
        if (showchildren) {
          params.append("showchildren", showchildren);
        }
        if (tagParam) {
          params.append("tag", tagParam);
        }
        if (params.toString()) {
          url += `?${params.toString()}`;
        }
      }
      const response = await apiClient.get(url);
      setRecipes(response.data);
    } catch (err) {
      console.error("Failed to fetch recipes:", err);
    }
  };

  // Fetch recipes on mount and when showchildren, creator, or tag changes
  useEffect(() => {
    fetchRecipes();
  }, [showchildren, creator, tagParam]);

  // Clear showchildren param when searching
  useEffect(() => {
    if (searchQuery && showchildren) {
      setSearchParams({});
    }
  }, [searchQuery, showchildren, setSearchParams]);

  const handleRecipeFormClose = async (success) => {
    setRecipeFormOpen(false);
    if (success) {
      // Refresh recipes list after successful creation
      await fetchRecipes();
    }
  };

  // Filter recipes by search query (case-insensitive)
  const filteredRecipes = recipes.filter((recipe) =>
    recipe.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleVariantCountClick = (e, parentUuid) => {
    e.stopPropagation(); // Prevent row click
    setSearchParams({ showchildren: parentUuid });
  };

  const formatTime = (minutes) => {
    if (minutes < 60) {
      return `${minutes} minutes`;
    }
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;
    if (mins === 0) {
      return `${hours} hour${hours > 1 ? "s" : ""}`;
    }
    return `${hours} hour${hours > 1 ? "s" : ""} ${mins} minute${mins > 1 ? "s" : ""}`;
  };

  return (
    <Box>
      <Typography 
        variant="h3" 
        gutterBottom 
        className="vintage-script-header"
      >
        {creator ? `Recipes by ${creator}` : tagParam ? `Recipes tagged: ${tagParam.split(",").join(", ")}` : "Recipes"}
      </Typography>
      {tagParam && (
        <Box sx={{ mb: 2 }}>
          <Button
            variant="outlined"
            size="small"
            onClick={() => {
              const newParams = new URLSearchParams(searchParams);
              newParams.delete("tag");
              setSearchParams(newParams);
            }}
          >
            Clear tag filter
          </Button>
        </Box>
      )}

      <TextField
        fullWidth
        label="Search Recipes"
        value={searchQuery}
        onChange={(e) => setSearchQuery(e.target.value)}
        placeholder="Search by name"
        variant="outlined"
        margin="normal"
        className="crop-search-field"
      />

      <TableContainer component={Paper} className="crops-table">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell className="column-name">Name</TableCell>
              <TableCell className="column-serves">Serves</TableCell>
              <TableCell className="column-time">Time</TableCell>
              {!showchildren && <TableCell className="column-variants">Variants</TableCell>}
            </TableRow>
          </TableHead>
          <TableBody>
            {user && (
              <TableRow 
                className="add-crop-row"
                onClick={() => setRecipeFormOpen(true)}
                sx={{ 
                  cursor: "pointer",
                  '&:hover': {
                    backgroundColor: 'rgba(201, 169, 97, 0.1) !important'
                  }
                }}
              >
                <TableCell className="column-name" colSpan={showchildren ? 3 : 4}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AddIcon sx={{ fontSize: '1.2rem', color: 'var(--color-sepia)' }} />
                    <Typography sx={{ fontFamily: 'var(--font-serif)', color: 'var(--color-sepia)', fontStyle: 'italic' }}>
                      Add Recipe
                    </Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
            {filteredRecipes.map((recipe) => (
              <TableRow
                key={recipe.recipe_uuid}
                onClick={() => navigate(getRecipeUrl(recipe))}
                style={{ cursor: "pointer" }}
                hover
              >
                <TableCell className="column-name">{recipe.name}</TableCell>
                <TableCell className="column-serves">{recipe.serves_amount}</TableCell>
                <TableCell className="column-time">{formatTime(recipe.time)}</TableCell>
                {!showchildren && (
                  <TableCell className="column-variants">
                    {recipe.variant_count > 0 ? (
                      <Link
                        component="button"
                        variant="body2"
                        onClick={(e) => handleVariantCountClick(e, recipe.recipe_uuid)}
                        sx={{ textDecoration: "underline", cursor: "pointer" }}
                      >
                        {recipe.variant_count}
                      </Link>
                    ) : (
                      0
                    )}
                  </TableCell>
                )}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Recipe Form Modal */}
      <RecipeForm
        open={recipeFormOpen}
        onClose={handleRecipeFormClose}
        recipe={null}
        parentRecipeUuid={null}
      />
    </Box>
  );
};

export default Recipes;


